import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import { BuyPage } from "@/routes/BuyPage";
import { ClaimPage } from "@/routes/ClaimPage";
import { LoginPage } from "@/routes/LoginPage";
import { ForgotPasswordPage } from "@/routes/ForgotPasswordPage";
import { ResetPasswordPage } from "@/routes/ResetPasswordPage";
import { RegisterPage } from "@/routes/RegisterPage";
import { TwoFactorPage } from "@/routes/TwoFactorPage";
import { InvitePage } from "@/routes/InvitePage";
import { AuthedLayout } from "@/routes/AuthedLayout";
import { OnboardingPage } from "@/routes/OnboardingPage";
import { OrgShell } from "@/routes/OrgShell";
import { RootIndexRedirect } from "@/routes/RootIndexRedirect";
import { GitHubConnectedPage } from "@/routes/GitHubConnectedPage";
import { LinearConnectedPage } from "@/routes/LinearConnectedPage";
import { SlackConnectedPage, TeamsConnectedPage } from "@/routes/ChannelConnectedPage";
import { AccountLinkPage } from "@/routes/AccountLinkPage";
import { SettingsRedirect } from "@/routes/SettingsRedirect";
import { RouteErrorFallback } from "@/components/ErrorBoundary";
import { clearPersistedData } from "@/lib/persistence";
import { AdminRouteError } from "@/features/admin/errors";
import {
  parseAuditSearch,
  parseJobSearch,
  parseOrgSearch,
  parseUserSearch,
  parseWorkerSearch,
} from "@/features/admin/list-search";

// Everything inside an org (worktrees, repos, workers, settings, and
// especially the CodeMirror/diff-heavy worktree screen) is only needed after
// sign-in + org selection, so it's split into its own chunk(s).
const WorktreesOverviewPage = lazyRouteComponent(
  () => import("@/routes/WorktreesOverviewPage"),
  "WorktreesOverviewPage",
);
const ReposPage = lazyRouteComponent(() => import("@/routes/ReposPage"), "ReposPage");
const WorkersPage = lazyRouteComponent(() => import("@/routes/WorkersPage"), "WorkersPage");
const AutomationsPage = lazyRouteComponent(() => import("@/routes/AutomationsPage"), "AutomationsPage");
const InboxPage = lazyRouteComponent(() => import("@/routes/InboxPage"), "InboxPage");
const AutomationDetailPage = lazyRouteComponent(
  () => import("@/routes/AutomationDetailPage"),
  "AutomationDetailPage",
);
const SettingsPage = lazyRouteComponent(() => import("@/routes/SettingsPage"), "SettingsPage");
const WorktreePage = lazyRouteComponent(() => import("@/routes/WorktreePage"), "WorktreePage");

// The super-user console is a separate, cross-org area. Lazy so none of it
// reaches the bundle of the (overwhelming majority of) users who can't open it.
const AdminShell = lazyRouteComponent(() => import("@/routes/AdminShell"), "AdminShell");
const AdminOverviewPage = lazyRouteComponent(() => import("@/routes/AdminOverviewPage"), "AdminOverviewPage");
const AdminOrgsPage = lazyRouteComponent(() => import("@/routes/AdminOrgsPage"), "AdminOrgsPage");
const AdminOrgDetailPage = lazyRouteComponent(() => import("@/routes/AdminOrgDetailPage"), "AdminOrgDetailPage");
const AdminUsersPage = lazyRouteComponent(() => import("@/routes/AdminUsersPage"), "AdminUsersPage");
const AdminUserDetailPage = lazyRouteComponent(() => import("@/routes/AdminUserDetailPage"), "AdminUserDetailPage");
const AdminWorkersPage = lazyRouteComponent(() => import("@/routes/AdminWorkersPage"), "AdminWorkersPage");
const AdminTasksPage = lazyRouteComponent(() => import("@/routes/AdminTasksPage"), "AdminTasksPage");
const AdminAuditPage = lazyRouteComponent(() => import("@/routes/AdminAuditPage"), "AdminAuditPage");
const AdminPromptsPage = lazyRouteComponent(() => import("@/routes/AdminPromptsPage"), "AdminPromptsPage");

/**
 * The last resort for a render error no route closer to it caught. Without
 * it TanStack replaces the whole app with its own unstyled notice.
 */
function RootError({ error, reset }: ErrorComponentProps) {
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <h1 className="auth-title">Something went wrong</h1>
        <p className="auth-subtitle">{error instanceof Error ? error.message : String(error)}</p>
        <div className="panel">
          <button className="btn btn-primary btn-block" onClick={reset}>
            Try again
          </button>
          <a className="btn btn-ghost btn-block" href="/">
            Back to Rivets
          </a>
        </div>
      </div>
    </div>
  );
}

export const rootRoute = createRootRoute({
  component: () => <Outlet />,
  errorComponent: RootError,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: RootIndexRedirect,
});

const buyRoute = createRoute({ getParentRoute: () => rootRoute, path: "/buy", component: BuyPage });
const claimRoute = createRoute({ getParentRoute: () => rootRoute, path: "/claim", component: ClaimPage });
const githubConnectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/github/connected",
  component: GitHubConnectedPage,
});
const linearConnectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/linear/connected",
  component: LinearConnectedPage,
});
// A native Slack/Teams install's return (lib/oauth-binding.ts
// `nativeOAuthVerifyUrl`), listed in apple-app-site-association: it opens the
// app where it is installed and lands here where it is not.
const slackConnectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/slack/connected",
  component: SlackConnectedPage,
});
const teamsConnectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/teams/connected",
  component: TeamsConnectedPage,
});
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage });
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: RegisterPage,
});
const twoFactorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/two-factor",
  component: TwoFactorPage,
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: InvitePage,
});

export const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authed",
  component: AuthedLayout,
});

const onboardingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/onboarding",
  component: OnboardingPage,
});

// A bare `/settings`, for links that cannot know which org the reader will
// sign in to.
const settingsRedirectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings",
  component: SettingsRedirect,
});

// Every account-link challenge (Slack, Teams, Linear). Signed in, and never
// redeemed on load: the page asks for an explicit confirmation.
const accountLinkRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/link/$token",
  component: AccountLinkPage,
});

export const orgRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/o/$orgId",
  component: OrgShell,
});

const orgIndexRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/",
  component: WorktreesOverviewPage,
});
const orgReposRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/repos",
  component: ReposPage,
});
const orgWorkersRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/workers",
  component: WorkersPage,
});
/** The member's mentions, newest first. */
const orgInboxRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/inbox",
  component: InboxPage,
});
const orgAutomationsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/automations",
  component: AutomationsPage,
});
/** An automation's own page: its prompt, its settings and its run history. */
const orgAutomationRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/automations/$automationId",
  component: AutomationDetailPage,
});
const orgSettingsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/settings",
  component: SettingsPage,
});
export const worktreeRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/wt/$worktreeId",
  validateSearch: (search: Record<string, unknown>) => ({
    chat: typeof search.chat === "string" ? search.chat : undefined,
    draft: search.draft === "chat" ? "chat" as const : undefined,
    inspector: search.inspector === "files" || search.inspector === "changes" || search.inspector === "pr" ? search.inspector : undefined,
    handoff: typeof search.handoff === "string" ? search.handoff : undefined,
  }),
  component: WorktreePage,
});

/** Sibling of `orgRoute`, not a child: the console has no org context. */
export const adminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin",
  component: AdminShell,
});

/** An admin page. Its error boundary is its own, inside `AdminShell`'s outlet,
 * so a page that fails to render keeps the console's sidebar around it. List
 * pages hold their filters, sort and page in the URL (features/admin/list-search.ts). */
function adminPage<S>(
  path: string,
  component: ReturnType<typeof lazyRouteComponent>,
  validateSearch?: (search: Record<string, unknown>) => S,
) {
  return createRoute({ getParentRoute: () => adminRoute, path, component, errorComponent: AdminRouteError, validateSearch });
}

const adminRouteWithChildren = adminRoute.addChildren([
  adminPage("/", AdminOverviewPage),
  adminPage("/orgs", AdminOrgsPage, parseOrgSearch),
  adminPage("/orgs/$orgId", AdminOrgDetailPage),
  adminPage("/users", AdminUsersPage, parseUserSearch),
  adminPage("/users/$userId", AdminUserDetailPage),
  adminPage("/workers", AdminWorkersPage, parseWorkerSearch),
  adminPage("/tasks", AdminTasksPage, parseJobSearch),
  adminPage("/audit", AdminAuditPage, parseAuditSearch),
  adminPage("/prompts", AdminPromptsPage),
]);

const orgRouteWithChildren = orgRoute.addChildren([
  orgIndexRoute,
  orgReposRoute,
  orgWorkersRoute,
  orgInboxRoute,
  orgAutomationsRoute,
  orgAutomationRoute,
  orgSettingsRoute,
  worktreeRoute,
]);

const authedRouteWithChildren = authedRoute.addChildren([
  onboardingRoute,
  settingsRedirectRoute,
  accountLinkRoute,
  orgRouteWithChildren,
  adminRouteWithChildren,
]);

const routeTree = rootRoute.addChildren([
  indexRoute,
  buyRoute,
  claimRoute,
  githubConnectedRoute,
  linearConnectedRoute,
  slackConnectedRoute,
  teamsConnectedRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  registerRoute,
  twoFactorRoute,
  inviteRoute,
  authedRouteWithChildren,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // A page that throws gets a way out instead of TanStack's bare "Something
  // went wrong!" — including clearing this browser's cache, for when the
  // cached data is what throws on every load (web #8).
  defaultErrorComponent: ({ error }) => <RouteErrorFallback error={error} onClearCache={clearPersistedData} />,
});
